import React, {
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Button, Card, Link, Typography, useMediaQuery } from "@mui/material";
import { useParams } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import PageContent from "../../../component/PageContent";
import InventoryTracking from "../../../api/InventoryTracking";
import ReactToPrint from "react-to-print";
import { useTheme } from "@emotion/react";
import momentPhoenix from "../../../helpers/momentPhoenix";

// To print multiple pages, wrap functional component with forwardRef
//"To print a functional component ensure it is wrapped with `React.forwardRef`, and ensure the forwarded ref is used. See the README for an example: https://github.com/gregnb/react-to-print#examples"
const PrintPullTruckTag = forwardRef((props, ref) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authToken } = useContext(AuthContext);
  const [details, setDetails] = useState(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const componentRef = useRef();
  const [isPrinting, setIsPrinting] = useState(false);
  // We store the resolve Promise being used in `onBeforeGetContent` here
  const promiseResolveRef = useRef(null);

  const getGrabbedDetails = async (controller) => {
    try {
      let data = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      };
      if (controller) {
        data.signal = controller.signal;
      }
      const response = await InventoryTracking.get(`/pulls/${id}`, data);
      console.log(response.data);
      setDetails(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    getGrabbedDetails(controller);
    return () => {
      controller.abort();
    };
  }, [id]);

  // We watch for the state to change here, and for the Promise resolve to be available
  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      // Resolves the Promise, letting `react-to-print` know that the DOM updates are completed
      promiseResolveRef.current();
    }
  }, [isPrinting]);

  const isPullRequest = !!details?.is_stage_request;
  const isApprovedOrRejected = !!details?.approved_by || !!details?.rejected_by;

  return (
    <PageContent>
      {details ? (
        <div>
          <div
            style={{
              marginTop: "20px",
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <ReactToPrint
              pageStyle={`
                    @page {
                    size: landscape;
                    margin: 20mm;
                    }
              `}
              trigger={() => {
                return <Button variant="contained">Print Details</Button>;
              }}
              content={() => componentRef.current}
              onBeforeGetContent={() => {
                return new Promise((resolve) => {
                  promiseResolveRef.current = resolve;
                  setIsPrinting(true);
                });
              }}
              onAfterPrint={() => {
                // Reset the Promise resolve so we can print again
                promiseResolveRef.current = null;
                setIsPrinting(false);
              }}
            />
          </div>
          <div
            ref={componentRef}
            style={{
              paddingLeft: 16,
              paddingRight: 16,
            }}
          >
            {/* START HERE */}
            <Typography
              variant="h1"
              textAlign="center"
              sx={{
                fontWeight: "bold",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: "2",
                WebkitBoxOrient: "vertical",
                color: isPrinting ? "#000000" : "inherit",
              }}
            >
              {/* {details.job_name
                ? `${details.projectid} - ${details.job_name}`
                : "[UNLISTED]"} */}
              {details.job_name}
            </Typography>

            {details.truck_tag_roof_type_1 && (
              <Typography
                variant="h2"
                textAlign="center"
                sx={{
                  fontWeight: "bold",
                  color: isPrinting ? "#000000" : "inherit",
                }}
              >
                {details.truck_tag_roof_type_1 ?? ""} -{" "}
                {details.truck_tag_roof_type_1_size ?? ""}
              </Typography>
            )}
            {details.truck_tag_roof_type_2 && (
              <Typography
                variant="h2"
                textAlign="center"
                sx={{
                  fontWeight: "bold",
                  color: isPrinting ? "#000000" : "inherit",
                }}
              >
                {details.truck_tag_roof_type_2 ?? ""} -{" "}
                {details.truck_tag_roof_type_2_size ?? ""}
              </Typography>
            )}

            {details.truck_tag_flashing_color && (
              <Typography
                variant="h2"
                textAlign="center"
                sx={{
                  fontWeight: "bold",
                  color: isPrinting ? "#000000" : "inherit",
                }}
              >
                {details.truck_tag_flashing_color ?? ""} Flashing
              </Typography>
            )}

            <Typography
              variant="h3"
              textAlign="center"
              sx={{
                fontWeight: "bold",
                color: isPrinting ? "#000000" : "inherit",
              }}
            >
              {details.job_address ?? ""}
            </Typography>
            <Typography
              variant="h3"
              textAlign="center"
              sx={{
                fontWeight: "bold",
                color: isPrinting ? "#000000" : "inherit",
              }}
            >
              Truck {details.truck_tag_truck_number ?? ""} -{" "}
              {details.truck_tag_crew ?? ""}
            </Typography>

            {/* END HERE */}
          </div>
        </div>
      ) : (
        <div style={{ height: "100%" }}>
          <div>Loading...</div>
        </div>
      )}
    </PageContent>
  );
});

export default PrintPullTruckTag;
